import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { fetchAuthSession } from "aws-amplify/auth";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useShallow } from "zustand/react/shallow";
import { useLoading } from "../../context/LoadingContext";
import { useExportTableToXLSX } from "../../hooks/useExportTableToXLSX";
import { useClinicalEvaluationStore } from "../../store/useClinicalEvaluationStore";
import { getSelectedStudyProductName } from "../../utils/clinicalStudy";
import { rememberClinicalExportSheet } from "../../utils/clinicalEvalExportTables";
import { checkPipelineLoss, extractPmid, normalizePmids } from "../../utils/clinicalEvalPipeline";
import { f1TableResults, f2TableResults } from "../../utils/Constants";
import { buildWsUrl } from "../../utils/wsUrl";
import Table from "../Table";
import { pmidColumn, searchTermColumn, titleColumn } from "./Columns";
import { RegulifyService } from "../../api/RegulifyService";

const WS_PING_INTERVAL_MS = 20000;

const WS_ERROR_MESSAGE = "There was a problem processing your Clinical Appraisal request. Please try again later.";

const UNHANDELLED_ERROR_MESSAGE = "Unhandelled Error fulfilling the request: "

const WS_URL = (token) =>
  buildWsUrl("generate_clinical_appraisal_suitability_table", token);

const getAuthToken = async () => {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString();
};

const startKeepAlive = (ws) =>
  setInterval(() => {
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "ping" }));
    }
  }, WS_PING_INTERVAL_MS);

function handleParsedMessage(parsed, { setLoading }) {
  switch (parsed.type) {
    case "error":
      console.error("Processing error:", parsed.message);
      toast.error(`Error: ${parsed.message}`);
      setLoading(false);
      return true;

    case "info":
      console.info("Processing update:", parsed.message);
      toast.info(`Update: ${parsed.message}`);
      return true;

    case "pong":
      console.info("Received ping/pong reply:", parsed.message);
      return true;

    default:
      return false;
  }
}

const columnHelper = createColumnHelper();

const initialColumns = [
  columnHelper.group({
    id: "blank",
    columns: [
      columnHelper.accessor("title", titleColumn),
      columnHelper.accessor((row) => row.pmid, pmidColumn),
      columnHelper.accessor((row) => row.searchTerm, searchTermColumn),
    ],
  }),
];

const Appraisal = ({ goToNextTab }) => {
  const { setLoading } = useLoading();
  const [data, setData] = useState();
  const [sorting, setSorting] = useState([]);
  const { selectedStudy, selectedAppraisalTables, pipelineStageData, setPipelineStage } =
    useClinicalEvaluationStore(
      useShallow((state) => ({
        selectedStudy: state.selectedStudy,
        selectedAppraisalTables: state.selectedAppraisalTables,
        pipelineStageData: state.pipelineStageData,
        setPipelineStage: state.setPipelineStage,
      })),
    );
  const studyName = getSelectedStudyProductName(selectedStudy);
  const [columns, setColumns] = useState(initialColumns);
  const exportTableToXLSX = useExportTableToXLSX();

  const table = useReactTable({
    data,
    columns,
    enableColumnPinning: true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: "includesString",
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
    initialState: {
      columnPinning: {
        left: ["title", "pmid", "searchTerm"],
        right: [],
      },
    },
  });

  useEffect(() => {
    const getRelevantResults = async (code) => {
      try {
         if (!studyName) {
          toast.error("studyName is missing!");
          return;
        }
        setLoading(true, "Generating appraisal grading… This may take a few minutes. Thank you for waiting.", "text-green-600");
        let keepAliveInterval;
        
        await RegulifyService.get_allowed_credits(5);

        const token = await getAuthToken();
        const ws = new WebSocket(WS_URL(token));

        ws.binaryType = "arraybuffer";

        ws.onopen = () => {
          keepAliveInterval = startKeepAlive(ws);

          const payload = {
            product_name: studyName,
            f1_method: selectedAppraisalTables?.f1 ? 1 : 0,
            f2_method: selectedAppraisalTables?.f2 ? 1 : 0,
          };

          ws.send(JSON.stringify(payload));
          toast.info("Processing started...");
        };

        ws.onmessage = (event) => {
          const { data } = event;

          try {
            const parsed = JSON.parse(data);

            if (handleParsedMessage(parsed, { setLoading })) { return; }

            if (parsed) {
              setUpData({ data: parsed });
            } else {
              console.log("Unexpected output content received:", parsed);
            }
          } catch {
            console.log("Non-JSON message received:", data);
          }
        };

        ws.onerror = () => {
          console.error(WS_ERROR_MESSAGE);
          toast.error(WS_ERROR_MESSAGE);
          setLoading(false);
        };

        ws.onclose = () => {
          console.log("WebSocket closed");
          clearInterval(keepAliveInterval);
          setLoading(false);
        };
      } catch (error) {
        console.error(UNHANDELLED_ERROR_MESSAGE, error);
        setLoading(false);
      }
    };
    getRelevantResults().then();
  }, []);

  const setUpData = (response) => {
    const updatedData = [];
    const updatedColumns = [];

    const updatedF1 = response?.data?.updated_f1 || [];
    const updatedF2 = response?.data?.updated_f2 || [];

    // =============================
    //  F1 SECTION
    // =============================
    if (selectedAppraisalTables?.f1 && updatedF1.length > 0) {
      //Find first valid F1 result with a non-empty response
      const firstValidF1 =
        updatedF1
          .flat()
          .find(
            (item) => Array.isArray(item?.response) && item.response.length > 0,
          ) || null;

      const firstF1Responses = firstValidF1?.response || [];

      if (firstF1Responses.length > 0) {
        const dynamicColumns = firstF1Responses.map((col, index) =>
          columnHelper.accessor("f1-" + index, {
            id: "f1-" + index,
            cell: (info) => {
              const value = info.getValue();
              const tableResult = f1TableResults?.[value];
              return (
                <div
                  className={`badge w-full text-center badge-${tableResult?.status || "neutral"}`}
                >
                  {tableResult?.label || "-"}
                </div>
              );
            },
            header: () => (
              <span className="block w-full break-words whitespace-pre-line">
                {col?.question || ""}
              </span>
            ),
            size: 300,
            meta: { className: "min-w-[300px] max-w-[300px]" },
          }),
        );

        const f1ColGroup = columnHelper.group({
          id: "f1",
          header: () => (
            <div className="w-full pl-4">
              As per table F1 of IMDRF MDCE WG/N56 final:2019
            </div>
          ),
          meta: { className: "bg-gray-200 border-r-10 border-white" },
          columns: [
            ...dynamicColumns,
            columnHelper.accessor((row) => row.f1Score, {
              id: "f1Score",
              cell: (info) => (
                <div className="w-full text-center">{info.getValue()}</div>
              ),
              header: () => (
                <span className="block w-full break-words whitespace-pre-line">
                  Result (Low value - good)
                </span>
              ),
              size: 150,
              meta: { className: "min-w-[150px] max-w-[150px]" },
            }),
          ],
        });

        updatedColumns.push(f1ColGroup);
      }

      //Build F1 row data
      updatedF1.forEach((f1Results) => {
        if (Array.isArray(f1Results) && f1Results.length > 0) {
          const resultData = f1Results.map((item) => {
            const dataItem = {
              title: item?.title || "",
              pmid: item?.pmid || "",
              searchTerm: item?.search_term || "",
              f1Score: item?.f1_score ?? "",
            };

            (item?.response || []).forEach(
              (res, index) =>
                (dataItem["f1-" + index] = res?.selected_option ?? ""),
            );

            return dataItem;
          });
          updatedData.push(...resultData);
        }
      });
    }

    // =============================
    //  F2 SECTION
    // =============================
    if (selectedAppraisalTables?.f2 && updatedF2.length > 0) {
      const firstValidF2 =
        updatedF2
          .flat()
          .find(
            (item) => Array.isArray(item?.response) && item.response.length > 0,
          ) || null;

      const firstF2Responses = firstValidF2?.response || [];

      if (firstF2Responses.length > 0) {
        const dynamicColumns = firstF2Responses.map((col, index) =>
          columnHelper.accessor("f2-" + index, {
            id: "f2-" + index,
            cell: (info) => {
              const value = info.getValue();
              const tableResult = f2TableResults?.[value];
              return (
                <div
                  className={`badge w-full text-center badge-${tableResult?.status || "neutral"}`}
                >
                  {tableResult?.label || "-"}
                </div>
              );
            },
            header: () => (
              <span className="block w-full break-words whitespace-pre-line">
                {col?.question || ""}
              </span>
            ),
            size: 300,
            meta: { className: "min-w-[300px] max-w-[300px]" },
          }),
        );

        const f2ColGroup = columnHelper.group({
          id: "f2",
          header: () => (
            <div className="w-full pl-4">
              As per table F2 of IMDRF MDCE WG/N56 final:2019
            </div>
          ),
          meta: { className: "bg-gray-300" },
          columns: [
            ...dynamicColumns,
            columnHelper.accessor((row) => row.f2Score, {
              id: "f2Score",
              cell: (info) => (
                <div className="w-full text-center">{info.getValue()}</div>
              ),
              header: () => (
                <span className="block w-full break-words whitespace-pre-line">
                  Result (Low value - good)
                </span>
              ),
              size: 150,
              meta: { className: "min-w-[150px] max-w-[150px]" },
            }),
          ],
        });

        updatedColumns.push(f2ColGroup);
      }

      //Merge or create F2 row data
      updatedF2.forEach((f2Results) => {
        if (Array.isArray(f2Results) && f2Results.length > 0) {
          if (selectedAppraisalTables?.f1 && updatedData.length > 0) {
            updatedData.forEach((item) => {
              const match = f2Results.find(
                (f2Item) => item.pmid === f2Item?.pmid,
              );
              if (match) {
                (match?.response || []).forEach(
                  (res, index) =>
                    (item["f2-" + index] = res?.selected_option ?? ""),
                );
                item["f2Score"] = match?.f2_score ?? "";
              }
            });
          } else {
            const resultData = f2Results.map((item) => {
              const dataItem = {
                title: item?.title || "",
                pmid: item?.pmid || "",
                searchTerm: item?.search_term || "",
                f2Score: item?.f2_score ?? "",
              };

              (item?.response || []).forEach(
                (res, index) =>
                  (dataItem["f2-" + index] = res?.selected_option ?? ""),
              );

              return dataItem;
            });
            updatedData.push(...resultData);
          }
        }
      });
    }

    const currentPmids = normalizePmids(
      updatedData.map((row) => extractPmid(row)),
    );
    const prevStage = pipelineStageData?.relevancy;
    if (prevStage?.studyName === studyName) {
      checkPipelineLoss({
        previousStage: "Clinical Relevancy",
        currentStage: "Appraisal",
        previousPmids: prevStage.pmids,
        currentPmids,
      });
    }
    setPipelineStage("appraisal", currentPmids, studyName);

    const exportColumns = [...initialColumns, ...updatedColumns];
    rememberClinicalExportSheet(studyName, "appraisal", {
      data: updatedData,
      columns: exportColumns,
    });
    setColumns(exportColumns);
    setData(updatedData);
  };

  return (
    <div className="flex h-full flex-col">
      <div className="h-[calc(100vh-180px)] px-4">
        {data && data.length > 0 && (
          <>
            <div className="mt-4 flex w-full justify-end gap-2">
              <input
                value={table.getState().globalFilter}
                className="input input-bordered w-full"
                onChange={(e) => table.setGlobalFilter(String(e.target.value))}
                placeholder="Filter results by any field"
              />
              <button
                className="btn tooltip tooltip-left"
                data-tip="Download the below data in excel format"
                onClick={() => exportTableToXLSX(data, columns)}
              >
                export
              </button>
              <button
                className="btn btn-primary tooltip tooltip-left"
                data-tip="Click to proceed"
                onClick={goToNextTab}
              >
                Next
              </button>
            </div>
            <div className="mt-4 max-h-[calc(100vh-280px)] overflow-x-auto overflow-y-auto">
              <Table table={table} className="text-black" />
              <div className="mt-6">
                <h2 className="mb-2 text-lg font-semibold">Color Legend</h2>
                <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-green-500"></div>
                    <span>D1 - Actual Device</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-green-500"></div>
                    <span>A1 - Same Use</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-green-500"></div>
                    <span>P1 - Applicable</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-green-500"></div>
                    <span>R1 - High quality</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-yellow-400"></div>
                    <span>D2 - Comparable Device</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-yellow-400"></div>
                    <span>A2 - Minor deviation</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-yellow-400"></div>
                    <span>P2 - Limited</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-yellow-400"></div>
                    <span>R2 - Minor deficiencies</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-red-400"></div>
                    <span>D3 - Other medical device</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-red-400"></div>
                    <span>A3 - Major deviation</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-red-400"></div>
                    <span>P3 - Different population</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-red-400"></div>
                    <span>R3 - Insufficient information</span>
                  </div>
                  {/* Yes Group - Green */}
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-green-500"></div>
                    <span>T1 - Yes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-green-500"></div>
                    <span>O1 - Yes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-green-500"></div>
                    <span>F1 - Yes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-green-500"></div>
                    <span>S1 - Yes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-green-500"></div>
                    <span>C1 - Yes</span>
                  </div>

                  {/* No Group - Red */}
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-red-400"></div>
                    <span>T2 - No</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-red-400"></div>
                    <span>O2 - No</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-red-400"></div>
                    <span>F2 - No</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-red-400"></div>
                    <span>S2 - No</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded bg-red-400"></div>
                    <span>C2 - No</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Appraisal;
